import {
  Paper,
  createStyles,
  TextInput,
  PasswordInput,
  Image,
  Title,
  Text,
  Anchor,
  Modal
} from "@mantine/core";
import hkiaLogo from '../loginImages/hkia_logo_phone@2x.png';
import bneLogo from '../loginImages/bne_logo_phone@2x.png';
import saabLogo from '../loginImages/saab_logotype_phone@2x.png';
import { useState } from 'react';
import { getDefaultUsername, getConfiguration } from './Helpers';
import { getDefaultIdentityProvider } from './Helpers';
import {
  AuthorizationServiceConfiguration,
  AuthorizationRequest, RedirectRequestHandler,
  FetchRequestor, LocalStorageBackend
} from '@openid/appauth';
import { BrowserRouter as Router, Route, Routes, Outlet } from 'react-router-dom';
import { generateRandom, deriveChallenge, NoHashQueryStringUtils } from './CryptoUtils';

const customCrypto = {
  generateRandom,
  deriveChallenge
};

const useStyles = createStyles((theme) => ({
  wrapper: {
    minHeight: 900,
    backgroundSize: "cover"
  },

  form: {
    borderRight: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[3]
    }`,
    minHeight: 900,
    maxWidth: 450,
    paddingTop: 80,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      maxWidth: "100%",
    },
  },

  title: {
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
  },

  logo: {
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    width: 120,
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

interface LoginProps {
  updateAuthenticated: (arg: any) => void
}

export const Login = (props: LoginProps) => {

  const [username, setUsername] =  useState(getDefaultUsername());
  const [password, setPassword] = useState('');
  const [showAuthError, setShowAuthError] = useState(false);
  const [showAcknowledgement, setShowAcknowledgement] = useState(false);
  const [showError, setShowError] = useState(false);
  const [identityProvider, setIdentityProvider] = useState(getDefaultIdentityProvider());
  const configuration = getConfiguration();

  const isCallback = location.pathname === '/tobt/callback'
  if ( showAcknowledgement && isCallback ) {
    setShowAcknowledgement(false);
  }

  function closeAuthErrorDialog() {
    setShowAuthError(false);
  }

  function closeShowAcknowledgement() {
    setShowAcknowledgement(false);
  }

  function closeCertErrorDialog() {
    setShowError(false);
  }

  function performLogin() {
    fetch('/externalApi/user/authentication/login?username='+username+'&password='+password, {
      method: "POST",
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'POST',
        'X-Saab-Application': 'aerobahn-tobt',
        'Access-Control-Allow-Headers': 'X-Requested-With, Content-Type, Authorization',
    } 
    }).then(function (response) {
      return response.json();
  })
  .then(function (result) {
      if (result && result.authenticated) {
        sessionStorage.setItem('sessionId', result.sessionId);
        sessionStorage.setItem('authenticated', result.authenticated);
        sessionStorage.setItem('username', result.username);
        
        if (configuration && configuration.settings && configuration.settings.useSystemAdminTimeSetting) {
          if (result.configurations["aerobahn.airport.timeZone"]) {
            sessionStorage.setItem('timezone', result.configurations["aerobahn.airport.timeZone"]);
          }
          if (result.configurations["aerobahn.airport.timeZoneLabel"]) {
            sessionStorage.setItem('timezoneLabel', result.configurations["aerobahn.airport.timeZoneLabel"]);
          }
          if (result.permissions["userSettings/displayTimesInAirportLocalTimeZone"] != null) {
            sessionStorage.setItem('isLocalAirportTime', result.permissions["userSettings/displayTimesInAirportLocalTimeZone"]);
          }
        }
        
        if (result.permissions["datablockFields/modifyManualFields/modifyManualTOBT"]) {
          sessionStorage.setItem('enableEdit', result.permissions["datablockFields/modifyManualFields/modifyManualTOBT"]);
        }
        props.updateAuthenticated(true);
      } else {
        setShowAuthError(true);
      }
    }).catch(
      e => {
        setShowError(true);
      }
    );
  }

  const authorizationHandler = new RedirectRequestHandler(new LocalStorageBackend(), NoHashQueryStringUtils, window.location, customCrypto);

  function performOidcLogin() {
    AuthorizationServiceConfiguration.fetchFromIssuer(process.env.REACT_APP_OAUTH2_AUTHORIZATION_SERVER as string, new FetchRequestor())
      .then((response) => {
        let extraParameters = {};
        if (identityProvider) {
          extraParameters = {
            "idp": identityProvider
          };
        }
    
        const authRequest = new AuthorizationRequest({
          client_id: process.env.REACT_APP_OAUTH2_CLIENT_ID as string,
          redirect_uri: process.env.REACT_APP_OAUTH2_REDIRECT_URI as string,
          scope: "openid",
          response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
          state: undefined,
          extras: extraParameters
        }, customCrypto, true);
        authorizationHandler.performAuthorizationRequest(response, authRequest);
      }).catch(
        e => {
          setShowError(true);
        }
      );
  }

  const { classes } = useStyles();
  return (
    <div className={classes.wrapper}>
      <Paper className={classes.form} radius="md" p="xl" >
          <Image
              radius="md"
              height={'80%'}
              width={'80%'}
              src={bneLogo}
          />

        <Title
          order={2}
          className={classes.title}
          align="left"
          mt="md"
          mb={40}
          style={{color:'#484848', fontWeight:700, fontSize:'xx-large', whiteSpace: 'nowrap', clear: 'both', overflow:'hidden', display:'inline-flex', marginBottom:'5%'}}
        >
          Aero<span style={{color:'#a45c30', fontWeight:500}}>bahn</span>
          <div style={{color:'black', fontSize:'12px', paddingLeft: '8px', paddingTop: '5px'}}><span style={{fontWeight:700}}>Airport </span><br></br>TOBT Manager</div>
        </Title>

        <TextInput
          label="Identity Provider"
          placeholder=""
          size="md"
          value={identityProvider ? identityProvider : ''}
          onChange={(event) => setIdentityProvider(event.currentTarget.value)}
        />
        <Text align="right" mt="md">
            <Anchor<"a">
                href="#"
                weight={700}
                onClick={performOidcLogin}
            >
              Log in
            </Anchor>
        </Text> <br></br>

        <TextInput
          label="Username"
          placeholder=""
          size="md"
          value={username ? username : ''}
          onChange={(event) => setUsername(event.currentTarget.value)}
        />
        <PasswordInput
          label="Password"
          placeholder=""
          mt="md"
          size="md"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
        />
        <Text align="right" mt="md">
            <Anchor<"a">
                href="#"
                weight={700}
                onClick={performLogin}
            >
              Log in
            </Anchor>
        </Text> <br></br>
        <Image
          radius="md"
          height={'60%'}
          width={'60%'}
          src={saabLogo}
          style={{float:'right', marginRight:'-25%'}}
        />
        <Modal opened={showAuthError} onClose={closeAuthErrorDialog} title="Authentication Error">
          <p style={{fontSize:'12.5px'}}>
              Please check username and password were entered correctly
          </p>
        </Modal>
        <Modal opened={showError} onClose={closeCertErrorDialog} title="Error">
          <p style={{fontSize:'12.5px'}}>
            There was an unexpected error logging in.  Please contact Customer Service.
          </p>
        </Modal>
        {/* TODO for mobile we would want to set fullScreen to true below */}
        <Modal opened={showAcknowledgement} onClose={closeAuthErrorDialog} withCloseButton={false} fullScreen={false}>
            <div>
              <p style={{fontSize:'12.5px'}}>
                <span style={{display:'block',textAlign:'center',fontSize:'17px'}}>
                  <strong> Hong Kong International Airport Authority</strong><br></br><br></br>
                </span>
                <strong>WARNING:</strong> Only authorized users are allowed to log in and / or use this computer system. By logging in, you represent that you are an authorized user and agree to be bound by the prevailing rules and regulations of the Authority’s Information Policy and Standards. Activities on this computer system are logged.
              </p>
              <Text style={{padding:'10px'}} align="center" mt="md">
                  <Anchor<"a">
                      href="#"
                      weight={700}
                      onClick={closeShowAcknowledgement}
                  >
                    Acknowledge
                  </Anchor>
              </Text>
            </div>
          </Modal>
      </Paper>
      <Outlet/>
    </div>
  );
}